import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import MuiLink from "@mui/material/Link";
import Portal from "@mui/material/Portal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import DefaultNavbarDropdown from "examples/Navbars/DefaultNavbar/DefaultNavbarDropdown";

function DefaultNavbarMobile({ routes, open, onClose }) {
    const [collapse, setCollapse] = useState("");

    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const handleSetCollapse = (name) =>
        collapse === name ? setCollapse(false) : setCollapse(name);

    const renderNavbarItems = routes.map(
        ({ name, icon, collapse: routeCollapses, href, route, collapse: navCollapse }) => (
            <DefaultNavbarDropdown
                key={name}
                name={name}
                icon={icon}
                collapseStatus={name === collapse}
                onClick={() => handleSetCollapse(name)}
                href={href}
                route={route}
                collapse={Boolean(navCollapse)}
            >
                <MKBox>
                    {routeCollapses?.map((item) => (
                        <MKBox key={item.name} px={1.5}>
                            {item.collapse ? (
                                <>
                                    <MKTypography
                                        display="block"
                                        variant="button"
                                        fontWeight="bold"
                                        textTransform="capitalize"
                                        py={0.75}
                                        px={0.5}
                                        noWrap
                                    >
                                        {item.name}
                                    </MKTypography>
                                    {item.collapse.map((el) => (
                                        <MKTypography
                                            key={el.name}
                                            component={el.route ? Link : MuiLink}
                                            to={el.route || ""}
                                            href={el.href || ""}
                                            target={el.href ? "_blank" : ""}
                                            rel="noreferrer"
                                            minWidth="11.25rem"
                                            display="block"
                                            variant="button"
                                            color="text"
                                            textTransform="capitalize"
                                            fontWeight="regular"
                                            py={0.625}
                                            px={1.5}
                                            noWrap
                                            sx={({
                                                palette: { grey, dark },
                                                borders: { borderRadius },
                                            }) => ({
                                                borderRadius: borderRadius.md,
                                                cursor: "pointer",
                                                transition: "all 300ms linear",
                                                "&:hover": {
                                                    backgroundColor: grey[200],
                                                    color: dark.main,
                                                },
                                            })}
                                        >
                                            {el.name}
                                        </MKTypography>
                                    ))}
                                </>
                            ) : (
                                <MKBox
                                    key={item.key}
                                    display="block"
                                    component={item.route ? Link : MuiLink}
                                    to={item.route || ""}
                                    href={item.href || ""}
                                    target={item.href ? "_blank" : ""}
                                    rel="noreferrer"
                                    sx={({
                                        palette: { grey, dark },
                                        borders: { borderRadius },
                                    }) => ({
                                        borderRadius: borderRadius.md,
                                        cursor: "pointer",
                                        transition: "all 300ms linear",
                                        py: 1,
                                        px: 1.5,
                                        "&:hover": {
                                            backgroundColor: grey[200],
                                            color: dark.main,
                                            "& *": { color: dark.main },
                                        },
                                    })}
                                >
                                    <MKTypography
                                        display="block"
                                        variant="button"
                                        fontWeight="bold"
                                        textTransform="capitalize"
                                        noWrap
                                    >
                                        {item.name}
                                    </MKTypography>
                                    {item.description && (
                                        <MKTypography
                                            display="block"
                                            variant="button"
                                            color="text"
                                            fontWeight="regular"
                                            noWrap
                                        >
                                            {item.description}
                                        </MKTypography>
                                    )}
                                </MKBox>
                            )}
                        </MKBox>
                    ))}
                </MKBox>
            </DefaultNavbarDropdown>
        )
    );

    if (!open) return null;

    return (
        <Portal>
            <MKBox
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: (t) => t.zIndex.modal + 1,
                    bgcolor: "rgba(255,255,255,0.94)",
                    backdropFilter: "blur(6px)",
                    display: "flex",
                    justifyContent: "center",
                    width: "100vw",
                    height: "100dvh",
                    overflow: "hidden",
                }}
            >
                <MKBox
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        height: "100%",
                        mx: "auto",
                        px: 2,
                        py: 2,
                        boxSizing: "border-box",
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        position: "relative",
                    }}
                >
                    {/* HEADER sticky cu X (nu se suprapune peste listă) */}
                    <MKBox
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 1,
                            mb: 1,
                            bgcolor: "rgba(255,255,255,0.95)",
                            backdropFilter: "blur(6px)",
                            borderRadius: 1,
                        }}
                    >
                        {/* (opțional) logo mic în stânga */}
                        <MKTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ pl: 1 }}
                        ></MKTypography>

                        <IconButton
                            aria-label="Închide"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{ mr: 0.5 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </MKBox>

                    {/* Lista – începe sub header, deci X nu mai apasă “Acasă” */}
                    <MKBox sx={{ pt: 0 }}>{renderNavbarItems}</MKBox>
                </MKBox>
            </MKBox>
        </Portal>
    );
}

DefaultNavbarMobile.propTypes = {
    routes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired, // 👈 adăugat
};

export default DefaultNavbarMobile;
